import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { TrendingUp, Users, CheckCircle, QrCode, ArrowRight, BarChart, FileText } from 'lucide-react';
import { ActivityFeed } from '../components/ActivityFeed';
import { OnlineTeamMembers } from '../components/OnlineTeamMembers';
import { RecentAuditWidget } from '../components/RecentAuditWidget';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { useNotifications } from '../hooks/useNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VendorDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const { connected, events, onlineUsers } = useNotifications();

  const fetchOverview = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(response.data);
      // Redirect new vendors to onboarding
      if (response.data.total_qr_codes === 0) {
        navigate('/onboarding');
        return;
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SkipLink />
        <Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="text-center" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8] mx-auto" aria-hidden="true"></div>
            <p className="mt-4 text-[#475569]">Loading dashboard...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SkipLink />
      <Header />
      
      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome back, {user?.name}! 👋
            </h1>
            <p className="text-[#475569]">Here's an overview of your AI interview performance</p>
            <Button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/dashboard/report`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'vendor-dashboard-report.pdf');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('PDF report downloaded!');
                } catch { toast.error('Failed to download report'); }
              }}
              variant="outline"
              size="sm"
              className="mt-3 rounded-full"
              data-testid="dashboard-pdf-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              Download PDF Report
            </Button>
          </div>

          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
            >
              <Card className="p-6 border border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#475569]">Active Interviews</span>
                  <QrCode className="h-5 w-5 text-[#1d4ed8]" aria-hidden="true" />
                </div>
                <p className="text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {overview?.total_qr_codes || 0}
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-6 border border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#475569]">Total Views</span>
                  <Users className="h-5 w-5 text-[#1d4ed8]" aria-hidden="true" />
                </div>
                <p className="text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {overview?.total_views || 0}
                </p>
                <p className="text-xs text-[#475569] mt-1">
                  {overview?.recent_views || 0} in last 7 days
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 border border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#475569]">Total Submissions</span>
                  <CheckCircle className="h-5 w-5 text-green-600" aria-hidden="true" />
                </div>
                <p className="text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {overview?.total_submissions || 0}
                </p>
                <p className="text-xs text-[#475569] mt-1">
                  {overview?.recent_submissions || 0} in last 7 days
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="p-6 border border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#475569]">Completion Rate</span>
                  <TrendingUp className="h-5 w-5 text-[#1d4ed8]" aria-hidden="true" />
                </div>
                <p className="text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {overview?.overall_completion_rate || 0}%
                </p>
              </Card>
            </motion.div>
          </div>

          {/* Top Performing QR Codes */}
          <section className="mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Top Performing Forms
              </h2>
              <Link to="/qr-codes">
                <Button variant="ghost" className="rounded-full">
                  View All
                  <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                </Button>
              </Link>
            </div>

            {overview?.top_qr_codes && overview.top_qr_codes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {overview.top_qr_codes.map((qr, index) => (
                  <motion.div
                    key={qr.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card
                      className="p-6 border border-[#E2E8F0] hover:border-[#1d4ed8] transition-all cursor-pointer"
                      onClick={() => navigate(`/qr-codes/${qr.id}/analytics`)}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-lg line-clamp-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {qr.title}
                        </h3>
                        <BarChart className="h-5 w-5 text-[#1d4ed8]" aria-hidden="true" />
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-[#475569]">Views</span>
                          <p className="font-bold text-lg">{qr.views}</p>
                        </div>
                        <div>
                          <span className="text-[#475569]">Submissions</span>
                          <p className="font-bold text-lg text-green-600">{qr.submissions}</p>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center border border-[#E2E8F0]">
                <QrCode className="h-12 w-12 text-[#475569] mx-auto mb-4" aria-hidden="true" />
                <p className="text-[#475569] mb-4">No QR codes yet. Create your first one!</p>
                <Link to="/qr-codes">
                  <Button className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]">
                    Create QR Code
                  </Button>
                </Link>
              </Card>
            )}
          </section>

          {/* Live Activity Feed */}
          <section className="mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <ActivityFeed wsEvents={events} connected={connected} />
              </div>
              <div className="space-y-6">
                <OnboardingChecklist />
                <RecentAuditWidget wsEvents={events} />
                <OnlineTeamMembers onlineUsers={onlineUsers} connected={connected} />
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};
